import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { schema, uiSchema, translateSchema } from './config';
import BuilderForm from '../FormCarbon';
import NotFound from '../NotFound';
import DataTable from '../DataTable';
import { postTempConfigFile, setTempConfigFile } from '../../../actions';
import { NestedDataTable } from '../DataTable/components';
import funcTranslateHelper from '../../../i18next/funcTranslateHelper';

const Stages = ({ configFile, tempConfigFile, saveTempConfigFile }) => {
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({});

  const config = tempConfigFile || configFile;
  const nestedStages = getNestedStages(config.stages) || []; // stages that have items or an action can be applied

  const stagesWithRoles = config.stages
    ? config.stages.reduce((acc, current) => {
      if (current.roles && current.roles.length) { acc.push(parseStageRoles(current)); }
      return acc;
    }, [])
    : [];
  const rows = stagesWithRoles.map(e => parseStages(e)); // only root stages

  const headers = [
    { header: 'Estado', key: 'name' },
    { header: 'Roles asociados', key: 'roles' }
  ];

  useEffect(() => {
    translateSchema(schema);
  }, []);

  function parseStages (stage) {
    return {
      id: stage.id,
      name: stage.name,
      roles: stage.roles
    };
  }

  const toggleForm = () => {
    setOpen(!open);
    if (open) setFormData({});
  };

  const onSubmit = ({ formData }) => {
    const { stage, roles } = formData;
    const selectedStage = getStageByPath(stage.split('.'));
    selectedStage.roles = roles;
    config.stages.forEach(stage => calculateRoles(stage));
    saveConfig({ ...config });
  };

  const onDelete = id => {
    let selectedStage = nestedStages.find(stage => stage.id === id);
    selectedStage = getStageByPath(selectedStage.pathId.split('.'));
    delete selectedStage.roles;
    config.stages.forEach(stage => calculateRoles(stage));
    saveConfig(config);
  };

  const openEdit = id => {
    const stage = nestedStages.find(stage => stage.id === id);
    const formData = { stage: stage.pathId, roles: stage.roles };
    setFormData(formData);
    toggleForm();
  };

  /**
   * Get stages to which new roles will be added. (Only the most nested stages in root stages)
   * @param {Object} stages
   * @param {String} pathName
   * @param {String} pathId
   */
  function getNestedStages (stages, pathName = '', pathId = '') {
    if (!stages) return;

    const array = [];
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      const currentPathName = pathName ? `${pathName}.${stage.name}` : stage.name;
      const currentPathId = pathId ? `${pathId}.${stage.id}` : stage.id;
      if (Object.prototype.hasOwnProperty.call(stage, 'stages')) {
        array.push(
          getNestedStages(stage.stages, currentPathName, currentPathId)
        );
      } else {
        array.push({
          ...stage,
          pathName: currentPathName,
          pathId: currentPathId
        });
      }
    }

    return array.flat();
  }

  /**
   * Return stage and its children with parsed roles (readable)
   * @param {Object} stages
   */
  function parseStageRoles (stage) {
    if (stage && stage.roles && stage.stages && config.roles && config.roles.length > 0) {
      const roles = stage.roles.map(
        roleName => {
          const roleFind = config.roles.find(e => e.name === roleName);
          return roleFind ? roleFind.name : undefined;
        }
      );
      const stages = stage.stages.reduce((acc, current) => {
        const result = parseStageRoles(current);
        if (result) acc.push(result);
        return acc;
      }, []);
      return { ...stage, roles, stages };
    } else if (stage.roles && config.roles) {
      const roles = stage.roles.map(
        roleName => {
          const roleFind = config.roles.find(e => e.name === roleName);
          return roleFind ? roleFind.name : undefined;
        }
      );
      return { ...stage, roles };
    }
  }

  function calculateRoles (stage) {
    if (Object.prototype.hasOwnProperty.call(stage, 'stages')) {
      stage.roles = [
        ...new Set(stage.stages.map(e => calculateRoles(e)).flat())
      ];
      return stage.roles;
    } else if (stage.roles) {
      return stage.roles;
    }
    return [];
  }

  function getStageByPath (path) {
    let stage = config;
    for (let i = 0; i < path.length; i++) {
      stage = stage.stages.find(e => e.id === path[i]);
    }
    return stage;
  }

  const saveConfig = async config => {
    const res = await postTempConfigFile(config);
    if (res.message === 'OK') {
      saveTempConfigFile(config);
      setNotification({
        type: 'success',
        message: funcTranslateHelper('Builder.Message.realizado')
      });
    } else {
      setNotification({
        type: 'error',
        message: funcTranslateHelper('Builder.Message.error')
      });
    }
    setTimeout(() => setNotification(null), 2000);
    if (open) toggleForm();
  };

  if (
    !config.roles ||
    !config.roles.length ||
    !config.stages ||
    !config.stages.length
  ) {
    return (
      <NotFound
        title='Stages'
        subtitle='Subtitle'
        type='no-req'
        message={funcTranslateHelper('Builder:Stages:needRoles')}
      />
    );
  }

  // Add stages and roles to schema
  const stagesToForm = nestedStages.reduce(
    (acc, current) => {
      const { pathIds, pathNames } = acc;
      pathIds.push(current.pathId);
      pathNames.push(current.pathName);
      return acc;
    },
    { pathIds: [], pathNames: [] }
  );

  schema.properties.stage.enum = [''].concat(stagesToForm.pathIds);
  schema.properties.stage.enumNames = [funcTranslateHelper('Builder:Stages:form:chooseOption')].concat(
    stagesToForm.pathNames
  );
  schema.properties.roles.items.enum = config.roles.map(role => role.name);
  schema.properties.roles.items.enumValues = config.roles.map(
    role => role.name
  );

  if (open) {
    return (
      <BuilderForm
        onAdd={onSubmit}
        onClose={toggleForm}
        onEdit={onSubmit}
        open={open}
        title={funcTranslateHelper('Builder.Stages.form.titulo')}
        subtitle={funcTranslateHelper('Builder.Stages.form.subtitulo')}
        schema={schema}
        uiSchema={uiSchema}
        formData={formData}
        primaryButtonText={funcTranslateHelper('Guardar')}
        secondaryButtonText={funcTranslateHelper('Cancelar')}
      />
    );
  }

  if (!rows.length) {
    return (
      <NotFound
        title={funcTranslateHelper('Builder.Stages.titulo')}
        subtitle={funcTranslateHelper('Builder.Stages.subtitulo')}
        handleOpen={toggleForm}
        notification={notification}
        message={funcTranslateHelper('Builder:Stages:newStages')}
      />
    );
  }

  return (
    <div className='data-table-container'>
      <DataTable
        title={funcTranslateHelper('Builder.Stages.titulo')}
        subtitle={funcTranslateHelper('Builder.Stages.subtitulo')}
        headers={headers}
        rows={rows}
        handleOpen={toggleForm}
        onDelete={onDelete}
        allowEdit={false}
        notification={notification}
        hasExpandableRows
        renderExpandable={id => (
          <NestedDataTable
            id={id}
            rawData={stagesWithRoles}
            headers={headers}
            keyName='stages'
            removeHeaders
            allowEdit
            onEdit={openEdit}
            onDelete={onDelete}
          />
        )}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    configFile: state.configFile,
    tempConfigFile: state.tempConfigFile
  };
};

const mapDispatchToProps = dispatch => {
  return {
    saveTempConfigFile: data => dispatch(setTempConfigFile(data))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Stages);
